import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { IOnboardingMilestone } from '@feathr/blackbox';
import { EOnboardingMilestone, EOnboardingTaskState } from '@feathr/blackbox';
import { Button, CardV2 as Card, EmptyState, Skeleton } from '@feathr/components';
import { useSupportChat } from '@feathr/extender/hooks';
import { useLocalUrl } from '@feathr/extender/state';

import Milestone from './Milestone';
import {
  getMilestoneConfig,
  getModalComponent,
  shouldMilestoneOpenModal,
} from './Milestones.types';

export interface IMilestoneHint {
  milestone: EOnboardingMilestone;
  content: ReactNode;
}

interface IMilestonesProps {
  milestones: IOnboardingMilestone[];
  isLoading: boolean;
  hints?: IMilestoneHint[];
}

function renderSkeleton(): JSX.Element {
  return (
    <>
      {Object.values(EOnboardingMilestone).map((milestone) => (
        <Card key={`skeleton-${milestone}`} width={'full'}>
          <Card.Content addVerticalGap={true} padding={'relaxed'}>
            <Skeleton title={true} width={400} />
            <Skeleton paragraphs={2} width={600} />
          </Card.Content>
        </Card>
      ))}
    </>
  );
}

function Milestones({
  milestones,
  isLoading,
  hints = [],
}: Readonly<IMilestonesProps>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const history = useHistory();
  const { openSupportChat } = useSupportChat();
  const [activeModal, setActiveModal] = useState<EOnboardingMilestone | null>(null);

  function handleMilestoneClick(
    milestone: EOnboardingMilestone,
    state: EOnboardingTaskState,
  ): void {
    const config = getMilestoneConfig(milestone, t, localUrl);

    // If milestone is completed and has an actionUrl, navigate there
    if (state === EOnboardingTaskState.Completed && config.actionUrl) {
      history.push(config.actionUrl);
      return;
    }

    // Check if this milestone should open a modal (and is not completed)
    if (shouldMilestoneOpenModal(milestone, t, localUrl)) {
      // Generic modal handling - no need to modify for new modals
      setActiveModal(milestone);
      return;
    }

    // Default behavior for navigation-based milestones
    if (config.actionUrl) {
      history.push(config.actionUrl);
    }
  }

  const handleCloseModal = useCallback((): void => {
    // Defer the state update to avoid updating during render
    setTimeout(() => setActiveModal(null), 0);
  }, []);

  return !isLoading ? (
    <>
      {milestones.length ? (
        milestones.map(
          ({ milestone, time_remaining: timeRemaining, state, total_time: totalTime }) => {
            const config = getMilestoneConfig(milestone, t, localUrl);

            // Find any hint for this milestone
            const milestoneHint = hints.find((hint) => hint.milestone === milestone);

            return (
              <Milestone
                additionalContent={milestoneHint?.content}
                description={config.description}
                key={milestone}
                onClick={(): void => handleMilestoneClick(milestone, state)}
                progressiveFeature={config.progressiveFeature}
                remainingTime={timeRemaining}
                state={state}
                title={config.title}
                totalTime={totalTime}
              />
            );
          },
        )
      ) : (
        <EmptyState
          description={t(
            'There are no onboarding milestones to show. Contact support to help you get set up.',
          )}
          height={'full'}
          label={t('No milestones found')}
          width={'full'}
        >
          <Button onClick={openSupportChat} type={'primary'}>
            {t('Contact support')}
          </Button>
        </EmptyState>
      )}
      {activeModal &&
        ((): JSX.Element | null => {
          const ModalComponent = getModalComponent(activeModal, t, localUrl);
          return ModalComponent ? <ModalComponent onClose={handleCloseModal} /> : null;
        })()}
    </>
  ) : (
    renderSkeleton()
  );
}

export default observer(Milestones);
